import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Checkbox,
  CircularProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  Edit as EditIcon,
  LockReset as LockResetIcon,
  LockOpen as LockOpenIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import PasswordResetLinkDialog from '../components/PasswordResetLinkDialog';

interface Employee {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  office?: string;
  position: string;
  role: string;
  accountType?: 'corporate' | 'invited';
  isActive: boolean;
  lastLogin: string;
  createdAt: string;
  isLocked: boolean;
  loginAttempts: number;
  lockedUntil: string | null;
  company: {
    name: string;
  };
}

interface EmployeeDetail extends Employee {
  progress: Array<{
    _id: string;
    status: string;
    progress: number;
    score: number;
    course: {
      title: string;
    };
    updatedAt: string;
  }>;
  evaluations: Array<{
    _id: string;
    score: number;
    completedAt: string;
    course: {
      title: string;
    };
  }>;
}

const CompanyEmployees: React.FC = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  /** all | never | logged — filtrar por último acceso a la plataforma */
  const [lastAccessFilter, setLastAccessFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  
  // Estados para filtros de fecha de registro
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Estados para selección de empleados
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [resendingEmails, setResendingEmails] = useState(false);
  
  // Estados para el diálogo de detalles
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDetail | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Estados para el diálogo de edición
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    department: '',
    office: '',
    position: '',
    role: 'employee',
    isActive: true
  });
  const [editLoading, setEditLoading] = useState(false);
  
  // Estados para departamentos
  const [departments, setDepartments] = useState<any[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  
  // Estados para sedes
  const [offices, setOffices] = useState<any[]>([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  
  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Estados para el diálogo de enlace de recuperación
  const [resetLinkDialogOpen, setResetLinkDialogOpen] = useState(false);
  const [resetLinkData, setResetLinkData] = useState<{
    employeeName: string;
    employeeEmail: string;
    resetLink: string;
  } | null>(null);

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchOffices();
  }, [page, searchTerm, departmentFilter, officeFilter, statusFilter, dateFrom, dateTo, lastAccessFilter, accountTypeFilter]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Parameters<typeof api.getCompanyEmployees>[0] = {
        page: 1,
        limit: 10000,
        search: searchTerm || undefined,
        department: departmentFilter || undefined,
        office: officeFilter || undefined,
        status: statusFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined
      };
      if (lastAccessFilter !== 'all') {
        params.lastAccess = lastAccessFilter;
      }
      if (accountTypeFilter !== 'all') {
        params.accountType = accountTypeFilter;
      }

      const response = await api.getCompanyEmployees(params);

      // Refuerzo en cliente: mismo criterio que la API (útil si el servidor aún no envía lastAccess en la consulta)
      let list = response.employees ?? [];
      if (lastAccessFilter === 'never') {
        list = list.filter((e) => !e.lastLogin);
      } else if (lastAccessFilter === 'logged') {
        list = list.filter((e) => Boolean(e.lastLogin));
      }

      const totalAdjusted = lastAccessFilter === 'all' ? response.pagination.total : list.length;

      setEmployees(list);
      setTotalPages(Math.max(1, Math.ceil(totalAdjusted / 10000)));
      setTotalEmployees(totalAdjusted);
      // Limpiar selección al recargar datos
      setSelectedIds(new Set());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando empleados');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const response = await api.getDepartments();
      setDepartments(response.filter((dept: any) => dept.isActive));
    } catch (err: any) {
      console.error('Error cargando departamentos:', err);
      // No mostrar error al usuario ya que no es crítico para la funcionalidad principal
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const fetchOffices = async () => {
    try {
      setOfficesLoading(true);
      const officesList = await api.getOffices();
      setOffices(officesList.filter((office: any) => office.isActive));
    } catch (err: any) {
      console.error('Error cargando sedes:', err);
      setOffices([]);
    } finally {
      setOfficesLoading(false);
    }
  };

  const handleViewEmployee = async (employeeId: string) => {
    try {
      setDetailLoading(true);
      const response = await api.getCompanyEmployeeDetail(employeeId);
      // El backend retorna { employee, progress, evaluations }
      const employeeDetail = {
        ...response.employee,
        progress: response.progress || [],
        evaluations: response.evaluations || []
      };
      setSelectedEmployee(employeeDetail);
      setDetailDialogOpen(true);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cargando detalles del empleado',
        severity: 'error'
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee);
    setEditFormData({
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      department: employee.department || '',
      office: employee.office || '',
      position: employee.position || '',
      role: employee.role,
      isActive: employee.isActive
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingEmployee(null);
    setEditFormData({
      firstName: '',
      lastName: '',
      email: '',
      department: '',
      office: '',
      position: '',
      role: 'employee',
      isActive: true
    });
  };

  const handleEditFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEmployee) return;

    setEditLoading(true);
    try {
      await api.updateCompanyEmployee(editingEmployee._id, editFormData);

      setSnackbar({
        open: true,
        message: 'Empleado actualizado exitosamente',
        severity: 'success'
      });

      handleCloseEditDialog();
      fetchEmployees();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error actualizando empleado',
        severity: 'error'
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleEmployeeStatus = async (employee: Employee) => {
    try {
      await api.updateCompanyEmployeeStatus(employee._id, !employee.isActive);

      setSnackbar({
        open: true,
        message: `Empleado ${employee.isActive ? 'desactivado' : 'activado'} exitosamente`,
        severity: 'success'
      });

      fetchEmployees();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error actualizando estado del empleado',
        severity: 'error'
      });
    }
  };

  const handleSendPasswordReset = async (employee: Employee) => {
    try {
      const response = await api.forgotPassword(employee.email);

      // Si el backend devuelve el token, mostrar el diálogo con el enlace
      if (response.resetToken) {
        const resetLink = `${process.env.REACT_APP_FRONTEND_URL || window.location.origin}/reset-password?token=${response.resetToken}`;
        
        setResetLinkData({
          employeeName: `${employee.firstName} ${employee.lastName}`,
          employeeEmail: employee.email,
          resetLink: resetLink
        });
        setResetLinkDialogOpen(true);
      } else {
        // Fallback: solo mostrar notificación
        setSnackbar({
          open: true,
          message: `Enlace de recuperación enviado a ${employee.email}`,
          severity: 'success'
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error enviando enlace de recuperación',
        severity: 'error'
      });
    }
  };

  const handleUnlockEmployee = async (employee: Employee) => {
    try {
      await api.unlockEmployee(employee._id);

      setSnackbar({
        open: true,
        message: `Cuenta de ${employee.firstName} ${employee.lastName} desbloqueada exitosamente`,
        severity: 'success'
      });

      fetchEmployees(); // Recargar la lista para actualizar el estado
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error desbloqueando cuenta',
        severity: 'error'
      });
    }
  };

  // Funciones de selección
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = new Set(employees.map((emp) => emp._id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectEmployee = (employeeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  };

  const isAllSelected = employees.length > 0 && selectedIds.size === employees.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < employees.length;

  // Reenviar correo de bienvenida
  const handleResendWelcomeEmails = async () => {
    if (selectedIds.size === 0) return;

    setResendingEmails(true);
    try {
      const response = await api.resendWelcomeEmails(Array.from(selectedIds));
      const data = response.data;
      
      setSnackbar({
        open: true,
        message: `Correos enviados: ${data.sent.length}${data.failed.length > 0 ? `, Fallidos: ${data.failed.length}` : ''}`,
        severity: data.failed.length > 0 ? 'error' : 'success'
      });

      setSelectedIds(new Set());
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error reenviando correos de bienvenida',
        severity: 'error'
      });
    } finally {
      setResendingEmails(false);
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusLabel = (isActive: boolean) => {
    return isActive ? 'Activo' : 'Inactivo';
  };

  const getLockStatusColor = (isLocked: boolean) => {
    return isLocked ? 'error' : 'success';
  };

  const getLockStatusLabel = (isLocked: boolean) => {
    return isLocked ? 'Bloqueada' : 'Desbloqueada';
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'company_admin': return 'Admin Empresa';
      case 'employee': return 'Empleado';
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'company_admin': return 'primary';
      case 'employee': return 'default';
      default: return 'default';
    }
  };

  const getAccountTypeLabel = (accountType?: string) => {
    return accountType === 'invited' ? 'Invitado (personal)' : 'Corporativo';
  };

  const getAccountTypeColor = (accountType?: string): 'primary' | 'secondary' => {
    return accountType === 'invited' ? 'secondary' : 'primary';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  if (loading && employees.length === 0) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Cargando empleados...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Gestión de Empleados
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filtros */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '2fr 1fr 1fr 1fr'
              },
              gap: 3,
              alignItems: 'center',
              mb: 2
            }}>
              <TextField
                fullWidth
                label="Buscar empleados"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Sede</InputLabel>
                <Select
                  value={officeFilter}
                  onChange={(e) => {
                    setOfficeFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Sede"
                  disabled={officesLoading}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {offices.map((office) => (
                    <MenuItem key={office._id} value={office.name}>
                      {office.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Departamento</InputLabel>
                <Select
                  value={departmentFilter}
                  onChange={(e) => {
                    setDepartmentFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Departamento"
                  disabled={departmentsLoading}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept._id} value={dept.name}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Estado"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Activos</MenuItem>
                  <MenuItem value="inactive">Inactivos</MenuItem>
                  <MenuItem value="locked">Bloqueados</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Filtros de fecha de registro */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '1fr 1fr 1fr 1fr'
              },
              gap: 3,
              alignItems: 'center'
            }}>
              <FormControl fullWidth>
                <InputLabel>Último acceso</InputLabel>
                <Select
                  value={lastAccessFilter}
                  onChange={(e) => {
                    setLastAccessFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Último acceso"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="never">Sin ingreso a la plataforma</MenuItem>
                  <MenuItem value="logged">Ya ingresaron</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Tipo de usuario</InputLabel>
                <Select
                  value={accountTypeFilter}
                  onChange={(e) => {
                    setAccountTypeFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Tipo de usuario"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="corporate">Corporativo</MenuItem>
                  <MenuItem value="invited">Invitado (personal)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Fecha registro desde"
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Fecha registro hasta"
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary">
                  Total: {totalEmployees} empleados
                </Typography>
                {(dateFrom || dateTo || lastAccessFilter !== 'all' || accountTypeFilter !== 'all') && (
                  <Button
                    size="small"
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                      setLastAccessFilter('all');
                      setAccountTypeFilter('all');
                    }}
                  >
                    Limpiar fechas y acceso
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Barra de acciones con selección */}
        {selectedIds.size > 0 && (
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {selectedIds.size} empleado{selectedIds.size !== 1 ? 's' : ''} seleccionado{selectedIds.size !== 1 ? 's' : ''}
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={resendingEmails ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
                    onClick={handleResendWelcomeEmails}
                    disabled={resendingEmails}
                  >
                    {resendingEmails ? 'Enviando...' : 'Reenviar correo de bienvenida'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Deseleccionar todo
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Tabla de empleados */}
        <Card>
          <CardContent>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isSomeSelected}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Empleado</TableCell>
                    <TableCell>Sede</TableCell>
                    <TableCell>Departamento</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Bloqueo</TableCell>
                    <TableCell>Último Acceso</TableCell>
                    <TableCell>Fecha Registro</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow 
                      key={employee._id} 
                      hover
                      selected={selectedIds.has(employee._id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(employee._id)}
                          onChange={() => handleSelectEmployee(employee._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {employee.firstName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {employee.firstName} {employee.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {employee.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {employee.office && <LocationOnIcon fontSize="small" color="action" />}
                          <Typography variant="body2">
                            {employee.office || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {employee.department || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getAccountTypeLabel(employee.accountType)}
                          color={getAccountTypeColor(employee.accountType)}
                          size="small"
                          variant={employee.accountType === 'invited' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRoleLabel(employee.role)}
                          color={getRoleColor(employee.role) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(employee.isActive)}
                          color={getStatusColor(employee.isActive)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getLockStatusLabel(employee.isLocked)}
                          color={getLockStatusColor(employee.isLocked)}
                          size="small"
                        />
                        {employee.isLocked && employee.loginAttempts > 0 && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {employee.loginAttempts} intentos
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(employee.lastLogin)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(employee.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Ver detalles">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleViewEmployee(employee._id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar empleado">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleEditEmployee(employee)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Enviar enlace de recuperación de contraseña">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => handleSendPasswordReset(employee)}
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                          {employee.isLocked && (
                            <Tooltip title="Desbloquear cuenta">
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={() => handleUnlockEmployee(employee)}
                              >
                                <LockOpenIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={employee.isActive ? 'Desactivar' : 'Activar'}>
                            <IconButton 
                              size="small" 
                              color={employee.isActive ? 'error' : 'success'}
                              onClick={() => handleToggleEmployeeStatus(employee)}
                            >
                              {employee.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Resumen de registros */}
            <Box display="flex" justifyContent="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                Mostrando {employees.length} de {totalEmployees} empleados
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Diálogo de detalles del empleado */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <PersonIcon />
            <Typography variant="h6">
              {selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : 'Detalles del Empleado'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Cargando detalles...</Typography>
            </Box>
          ) : selectedEmployee ? (
            <Box>
              {/* Información básica */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)'
                },
                gap: 3,
                mb: 3
              }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Información Personal
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{selectedEmployee.email}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Departamento
                    </Typography>
                    <Typography variant="body1">{selectedEmployee.department || 'N/A'}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Cargo
                    </Typography>
                    <Typography variant="body1">{selectedEmployee.position || 'N/A'}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Estado de la Cuenta
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Estado
                    </Typography>
                    <Chip
                      label={getStatusLabel(selectedEmployee.isActive)}
                      color={getStatusColor(selectedEmployee.isActive)}
                      size="small"
                    />
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Estado de Bloqueo
                    </Typography>
                    <Chip
                      label={getLockStatusLabel(selectedEmployee.isLocked)}
                      color={getLockStatusColor(selectedEmployee.isLocked)}
                      size="small"
                    />
                    {selectedEmployee.isLocked && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Intentos fallidos: {selectedEmployee.loginAttempts}
                        {selectedEmployee.lockedUntil && (
                          <span> • Bloqueado hasta: {formatDate(selectedEmployee.lockedUntil)}</span>
                        )}
                      </Typography>
                    )}
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Último Acceso
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedEmployee.lastLogin)}
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Fecha de Registro
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedEmployee.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Progreso de cursos */}
              <Typography variant="h6" gutterBottom>
                Progreso de Cursos
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Curso</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Progreso</TableCell>
                      <TableCell align="center">Puntuación</TableCell>
                      <TableCell align="center">Última Actualización</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedEmployee.progress.map((progress) => (
                      <TableRow key={progress._id}>
                        <TableCell>
                          <Typography variant="body2">
                            {progress.course.title}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={progress.status}
                            color={progress.status === 'completed' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {progress.progress}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {progress.score ? (
                            <Chip
                              label={`${progress.score}%`}
                              color={getScoreColor(progress.score) as any}
                              size="small"
                            />
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatDate(progress.updatedAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Evaluaciones */}
              <Typography variant="h6" gutterBottom>
                Evaluaciones Completadas
              </Typography>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Curso</TableCell>
                      <TableCell align="center">Puntuación</TableCell>
                      <TableCell align="center">Fecha de Completado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedEmployee.evaluations.map((evaluation) => (
                      <TableRow key={evaluation._id}>
                        <TableCell>
                          <Typography variant="body2">
                            {evaluation.course.title}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${evaluation.score}%`}
                            color={getScoreColor(evaluation.score) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatDate(evaluation.completedAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {selectedEmployee?.isLocked && (
            <Button 
              onClick={() => {
                handleUnlockEmployee(selectedEmployee);
                setDetailDialogOpen(false);
              }}
              variant="contained"
              color="success"
              startIcon={<LockOpenIcon />}
            >
              Desbloquear Cuenta
            </Button>
          )}
          <Button onClick={() => setDetailDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de edición de empleado */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <EditIcon />
            <Typography variant="h6">
              Editar Empleado
            </Typography>
          </Box>
        </DialogTitle>
        <form onSubmit={handleEditFormSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  label="Nombre"
                  value={editFormData.firstName}
                  onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  label="Apellido"
                  value={editFormData.lastName}
                  onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                  required
                  fullWidth
                />
              </Box>
              
              <TextField
                label="Email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
                fullWidth
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Sede</InputLabel>
                  <Select
                    value={editFormData.office}
                    onChange={(e) => setEditFormData({ ...editFormData, office: e.target.value })}
                    label="Sede"
                    disabled={officesLoading}
                  >
                    <MenuItem value="">
                      <em>Sin sede</em>
                    </MenuItem>
                    {offices.map((office) => (
                      <MenuItem key={office._id} value={office.name}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Departamento</InputLabel>
                  <Select
                    value={editFormData.department}
                    onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                    label="Departamento"
                    disabled={departmentsLoading}
                  >
                    <MenuItem value="">
                      <em>Sin departamento</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept._id} value={dept.name}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Cargo"
                value={editFormData.position}
                onChange={(e) => setEditFormData({ ...editFormData, position: e.target.value })}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                  label="Rol"
                >
                  <MenuItem value="employee">Empleado</MenuItem>
                  <MenuItem value="company_admin">Administrador de Empresa</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={editFormData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.value === 'active' })}
                  label="Estado"
                >
                  <MenuItem value="active">Activo</MenuItem>
                  <MenuItem value="inactive">Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} disabled={editLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={editLoading}>
              {editLoading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de enlace de recuperación */}
      {resetLinkData && (
        <PasswordResetLinkDialog
          open={resetLinkDialogOpen}
          onClose={() => {
            setResetLinkDialogOpen(false);
            setResetLinkData(null);
          }}
          employeeName={resetLinkData.employeeName}
          employeeEmail={resetLinkData.employeeEmail}
          resetLink={resetLinkData.resetLink}
        />
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CompanyEmployees;
